import React, { useCallback, useState, useEffect } from 'react';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { useFormik } from 'formik';
import cx from 'classnames';
import * as Yup from 'yup';
import SectionPlain from './SectionPlain';
import './RequestQuoteWizard.scss';
import {
  ADDITIONAL_INFORMATION_STEP_ID,
  PERSONAL_INFO_STEP_ID,
  REQUEST_QUOTE_FLOW,
  WHAT_SERVICES_INTERESTED_STEP_ID,
} from '../constants/requestQuote';
import RequestQuoteAnswer from './RequestQuoteAnswer';
import Button from './Button';
import RequestQuoteAdditionalInformationInput from './RequestQuoteAdditionalInformationInput';
import RequestQuotePersonalInformationInput from './RequestQuotePersonalInformationInput';
import { encode } from '../utils/encode';
import SuccessMessage from './SuccessMessage';

export const contactFormSchema = Yup.object().shape({
  additionalInformation: Yup.string()
    .max('1024', 'Your request is too long, lease enter a request with less than 1024 characters.')
    .required('Please describe how can we help your.'),
});

const contactInfoSchema = Yup.object().shape({
  fullName: Yup.string()
    .max(128, 'Your full name is too long')
    .required('Please enter your (name/full name), we need to know how to call you.'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  phone: Yup.string().max(128, 'Your phone is too long'),
  companyName: Yup.string().max(128, 'Your company name is too long'),
});

const RequestQuoteWizard = ({ icons: iconPaths }) => {
  const [currentStep, setCurrentStep] = useState(WHAT_SERVICES_INTERESTED_STEP_ID);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [isSuccessMessageVisible, setSuccessMessageVisible] = useState(false);
  const [isRequestQuoteCompleted, setRequestQuoteCompleted] = useState(false);
  const [isLoadingRequestQuoteSubmit, setIsLoadingRequestQuoteSubmit] = useState(false);
  const additionalChangesForm = useFormik({
    initialValues: {
      additionalInformation: '',
    },
    onSubmit: () => ({}),
    validateOnMount: true,
    validationSchema: contactFormSchema,
  });
  const personalInformationForm = useFormik({
    initialValues: {
      fullName: '',
      email: '',
      phone: '',
      companyName: '',
    },
    onSubmit: () => ({}),
    validateOnMount: true,
    validationSchema: contactInfoSchema,
  });

  const currentStepData = () => REQUEST_QUOTE_FLOW[currentStep];

  const handleCheckboxValueAdd = (checkedValues, value) => {
    if (checkedValues.includes(value)) {
      return checkedValues.filter((checkedValue) => checkedValue !== value);
    }
    return [...checkedValues, value];
  };

  const handleAnswerClick = useCallback(
    (event) => {
      event.stopPropagation();
      event.preventDefault();
      const { name, value } = event.target;

      setSelectedAnswers((prevSelectedAnswers) => ({
        ...prevSelectedAnswers,
        [currentStep]: value,
      }));

      setCurrentStep(name);
    },
    [currentStep, selectedAnswers]
  );

  const handleAnswerChange = useCallback(
    (event) => {
      const { value } = event.target;

      const selectedValues =
        selectedAnswers[currentStep] && selectedAnswers[currentStep].length
          ? selectedAnswers[currentStep]
          : [];

      setSelectedAnswers((prevSelectedAnswers) => ({
        ...prevSelectedAnswers,
        [currentStep]: handleCheckboxValueAdd(selectedValues, value),
      }));
    },
    [currentStep, selectedAnswers]
  );

  const goToPreviousStep = useCallback(() => {
    const completedSteps = Object.keys(selectedAnswers);
    const previousStep = completedSteps[completedSteps.length - 1];
    const lastToPreviousStep = completedSteps[completedSteps.length - 2];

    setSelectedAnswers((prevAnswers) => {
      const prevAnswersClone = { ...prevAnswers };

      delete prevAnswersClone[previousStep];

      return prevAnswersClone;
    });

    setCurrentStep(lastToPreviousStep);
  }, [currentStep, selectedAnswers]);

  const getCurrentPathStepPercentage = () => {
    if (currentStep === WHAT_SERVICES_INTERESTED_STEP_ID) {
      return 5;
    }

    if (currentStepData().isFinalStep) {
      return 100;
    }

    const answeredQuestionKeys = Object.keys(selectedAnswers);
    const firstAnswer = answeredQuestionKeys[0];

    if (firstAnswer === 'Other services') {
      return (100 / 6) * answeredQuestionKeys.length;
    }
    return (100 / 10) * answeredQuestionKeys.length;
  };

  const makeRequestReadable = () =>
    Object.entries(selectedAnswers).reduce((acc, [key, value]) => {
      if (key === PERSONAL_INFO_STEP_ID) {
        return {
          ...acc,
          'Full name': value.fullName,
          Email: value.email,
          Phone: value.phone,
          'Company name': value.companyName,
        };
      }

      return {
        ...acc,
        [REQUEST_QUOTE_FLOW[key].question]: value,
      };
    }, {});

  const requestQuote = useCallback(() => {
    personalInformationForm.setFieldTouched('email');
    personalInformationForm.setFieldTouched('fullName');

    if (!personalInformationForm.isValid) {
      return;
    }

    const readableAnswers = makeRequestReadable();

    const body = encode({
      'form-name': 'Request a quote',
      ...readableAnswers,
    });

    setIsLoadingRequestQuoteSubmit(true);

    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    })
      .then((response) => {
        if (response.status === 200) {
          setSuccessMessageVisible(true);
          setRequestQuoteCompleted(true);
        }
      })
      // eslint-disable-next-line no-console
      .catch((error) => console.error(error))
      .finally(() => setIsLoadingRequestQuoteSubmit(false));
  }, [selectedAnswers, personalInformationForm]);

  const handleContinueButtonClick = useCallback(() => {
    if (currentStep === ADDITIONAL_INFORMATION_STEP_ID) {
      additionalChangesForm.setFieldTouched('additionalInformation');

      if (additionalChangesForm.values.additionalInformation) {
        setCurrentStep(currentStepData().nextStep);
      }

      return;
    }

    setCurrentStep(currentStepData().nextStep);
  }, [currentStep, additionalChangesForm, personalInformationForm]);

  const renderWizardComponents = () => {
    switch (currentStep) {
      case ADDITIONAL_INFORMATION_STEP_ID:
        return (
          <RequestQuoteAdditionalInformationInput
            errors={additionalChangesForm.errors}
            touched={additionalChangesForm.touched}
            formik={additionalChangesForm}
          />
        );
      case PERSONAL_INFO_STEP_ID:
        return (
          <RequestQuotePersonalInformationInput
            errors={personalInformationForm.errors}
            touched={personalInformationForm.touched}
            formik={personalInformationForm}
          />
        );
      default:
        return (
          <div
            className={cx({
              'request-quote-wizard__answers-container': true,
              'request-quote-wizard__answers-container--single-answer':
                !currentStepData().multiselect,
              'request-quote-wizard__answers-container--multi-answer':
                currentStepData().multiselect,
            })}
          >
            {currentStepData().answers.map((answer) => (
              <RequestQuoteAnswer
                key={answer.title}
                title={answer.answer}
                icon={answer.icon}
                text={answer.text}
                nextStep={answer.nextStep}
                currentStep={currentStep}
                currentStepAnswers={selectedAnswers[currentStep]}
                iconPaths={iconPaths}
                multiselect={currentStepData().multiselect}
                onClick={handleAnswerClick}
                onChange={handleAnswerChange}
              />
            ))}
          </div>
        );
    }
  };

  const title = () => {
    if (isRequestQuoteCompleted) {
      return 'We will get in touch with you in a few hours';
    }
    return currentStepData().question;
  };

  useEffect(() => {
    if (!selectedAnswers[currentStep]) {
      setSelectedAnswers((prevSelectedAnswers) => ({
        ...prevSelectedAnswers,
        [currentStep]: null,
      }));
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStep !== ADDITIONAL_INFORMATION_STEP_ID) {
      return;
    }

    setSelectedAnswers((prevSelectedAnswers) => ({
      ...prevSelectedAnswers,
      [ADDITIONAL_INFORMATION_STEP_ID]: additionalChangesForm.values.additionalInformation,
    }));
  }, [additionalChangesForm.values]);

  useEffect(() => {
    if (currentStep !== PERSONAL_INFO_STEP_ID) {
      return;
    }

    setSelectedAnswers((prevSelectedAnswers) => ({
      ...prevSelectedAnswers,
      [PERSONAL_INFO_STEP_ID]: personalInformationForm.values,
    }));
  }, [personalInformationForm.values]);

  return (
    <div className="request-quote-wizard">
      <div
        className="request-quote-wizard__progress-bar"
        style={{ width: `${getCurrentPathStepPercentage()}%` }}
      />
      <SectionPlain className="request-quote-wizard__section">
        <h3 className="request-quote-wizard__title">{title()}</h3>
        {!isRequestQuoteCompleted && (
          <p className="body-2 request-quote-wizard__info-text">{currentStepData().infoText}</p>
        )}
        <SwitchTransition mode="out-in">
          <CSSTransition
            key={currentStepData().question}
            addEndListener={(node, done) => {
              setTimeout(() => {
                node.addEventListener('transitionend', done, false);
              }, 600);
            }}
            classNames="fadeQuote"
          >
            <div className="request-quote-wizard__answers-container-wrapper">
              {renderWizardComponents()}
            </div>
          </CSSTransition>
        </SwitchTransition>
        {!!Object.keys(selectedAnswers).length && (
          <div
            className={cx({
              'request-quote-wizard__action-btn-container': true,
              'request-quote-wizard__action-btn-container--multi': currentStepData().multiselect,
              'request-quote-wizard__action-btn-container--input':
                currentStep === PERSONAL_INFO_STEP_ID ||
                currentStep === ADDITIONAL_INFORMATION_STEP_ID,
            })}
          >
            {Object.keys(selectedAnswers).length > 1 && (
              <Button
                className="request-quote-wizard__back-btn"
                variant="third"
                onClick={goToPreviousStep}
                isNonGatsbyButton
              >
                Go back
              </Button>
            )}
            {(currentStepData().multiselect ||
              currentStep === ADDITIONAL_INFORMATION_STEP_ID ||
              currentStep === PERSONAL_INFO_STEP_ID) && (
              <Button
                onClick={() => {
                  if (!currentStepData().isFinalStep) {
                    handleContinueButtonClick();
                  } else {
                    requestQuote();
                  }
                }}
                isLoading={isLoadingRequestQuoteSubmit}
                isNonGatsbyButton
              >
                {currentStep === PERSONAL_INFO_STEP_ID ? 'Send a request' : 'Continue'}
              </Button>
            )}
          </div>
        )}
        <form name="Request a quote" method="POST" netlify-honeypot="bot-field" data-netlify="true">
          <input type="hidden" name="form-name" value="Request a quote" />
          <input type="hidden" name="What service are you interested in?" />
          <input type="hidden" name="What type of a website do you need?" />
          <input type="hidden" name="When would you like to start working with us?" />
          <input type="hidden" name="How much would you like to invest?" />
          <input type="hidden" name="What type of business the website is for?" />
          <input type="hidden" name="What services are you interested in?" />
          <input type="hidden" name="What other services you would like to get?" />
          <input type="hidden" name="How many products you offer?" />
          <input type="hidden" name="What type of website it is?" />
          <input type="hidden" name="What are the goals of the upgrade?" />
          <input type="hidden" name="Phone" />
          <input type="hidden" name="Full name" />
          <input type="hidden" name="Company name" />
          <input type="hidden" name="Email" />
          <input type="hidden" name="Additional information about the project" />
        </form>
      </SectionPlain>
      <SuccessMessage
        isActiveMsg={isSuccessMessageVisible}
        setIsActiveMsg={setSuccessMessageVisible}
      />
    </div>
  );
};

export default RequestQuoteWizard;
